// @flow

import React from 'react';
import { compose, withProps } from 'recompose';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash/fp';

import { normalizeLabel } from 'app/normalizers/decodeChallenge';
import { getChallenge } from 'app/api/challenges';
import withAPIData from 'app/hoc/withAPIData';
import { padding } from 'polished';
import Tappable from 'app/components/commons/Tappable';
import type { TabItem } from 'app/types';
import EntrantsContent from '../static/EntrantsContent';
import LabelsContent from '../static/LabelsContent';
import GamesContent from '../static/GamesContent';
import PrizesContent from '../static/PrizesContent';
import ScoringContent from '../static/ScoringContent';

type Props = {
  activeTabItem: ?TabItem,
  bonuses: Array<Object>,
  challengeId: string,
  countries: Array<Object>,
  entryCap: number,
  entryCount: number,
  games: Array<Object>,
  isLoading?: boolean,
  isRacing?: boolean,
  isUnlimited: boolean,
  labels: Array<Object>,
  maxEntriesPerPerson: number,
  onClose?: () => void,
  onFetchData: () => void,
  payoutMessage?: string,
  prizes?: Array<*>,
  scheme: string,
  scoring: Array<Object>,
  statusId: number
};

const defaultProps = {
  isLoading: false,
  isRacing: false,
  onClose: undefined,
  payoutMessage: undefined,
  prizes: []
};

const Wrapper = styled.div(({ theme }) => ({
  paddingBottom: theme.spacing.xsmall
}));

const CollapsibleBoxInner = styled.div(({ theme }) => ({
  paddingTop: theme.spacing.default,
  paddingBottom: theme.spacing.large,
  overflow: 'hidden',
  fontSize: theme.fontSize.small,
  ...padding(theme.spacing.small, theme.spacing.default),
  [theme.mediaQuery.desktopAndUp]: {
    ...padding(theme.spacing.default, theme.spacing.xxlarge)
  }
}));

const CollapseBoxTrigger = styled.div(
  {
    alignItems: 'center',
    borderRadius: 20,
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    textTransform: 'uppercase',
    width: 36
  },
  ({ theme }) => ({
    borderColor: theme.color.basis4,
    fontSize: theme.fontSize.xtiny
  })
);

const lazyLoadTabs = ['labels', 'scoring'];

class ChallengeInfoContent extends React.Component<Props> {
  props: Props;

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { activeTabItem, labels, onFetchData, scoring } = this.props;

    if (
      (isEmpty(labels) || isEmpty(scoring)) &&
      !lazyLoadTabs.includes(activeTabItem) &&
      lazyLoadTabs.includes(nextProps.activeTabItem)
    ) {
      onFetchData();
    }
  }

  render() {
    const {
      activeTabItem,
      bonuses = [],
      challengeId,
      countries,
      entryCap,
      entryCount,
      games,
      labels,
      isLoading,
      isRacing,
      isUnlimited,
      maxEntriesPerPerson,
      onClose,
      payoutMessage,
      prizes,
      scheme,
      scoring,
      statusId
    } = this.props;

    return (
      <Wrapper>
        <CollapsibleBoxInner>
          {activeTabItem === 'games' && (
            <GamesContent
              key="games"
              games={games}
              isRacing={isRacing}
              scheme={['white', 'light'].includes(scheme) ? 'light' : 'dark'}
            />
          )}
          {activeTabItem === 'scoring' && (
            <ScoringContent
              key="scoring"
              bonuses={bonuses}
              isLoading={isLoading}
              scoringInfo={scoring}
            />
          )}
          {activeTabItem === 'entries' && (
            <EntrantsContent
              key="entries"
              challengeId={challengeId}
              countries={countries}
              maxEntriesPerPerson={maxEntriesPerPerson}
              scheme={['white', 'light'].includes(scheme) ? 'light' : 'dark'}
              entryCap={entryCap}
              entryCount={entryCount}
              isUnlimited={isUnlimited}
              statusId={statusId}
            />
          )}
          {activeTabItem === 'prizes' && (
            <PrizesContent
              key="prizes"
              challengeId={challengeId}
              scheme={['white', 'light'].includes(scheme) ? 'light' : 'dark'}
              payoutMessage={payoutMessage}
              prizes={prizes}
            />
          )}
          {activeTabItem === 'labels' && (
            <LabelsContent
              key="labels"
              bonuses={bonuses}
              isLoading={isLoading}
              isRacing={isRacing}
              labels={labels}
            />
          )}
        </CollapsibleBoxInner>
        {onClose && (
          <Tappable onTap={onClose} component={CollapseBoxTrigger}>
            <span className="ion-chevron-up" />
          </Tappable>
        )}
      </Wrapper>
    );
  }
}

ChallengeInfoContent.defaultProps = defaultProps;

const apiWrapper = compose(
  withAPIData({
    fetcher: getChallenge
  }),
  withProps(
    ({
      challengeId,
      data: { labels: challengeLabels = [], statistic_categories: statisticCategories = [] } = {},
      labels,
      loading,
      onFetch,
      scoring
    }) => ({
      isLoading: loading,
      labels: labels.length === 0 ? challengeLabels.map(normalizeLabel) : labels,
      scoring: scoring.length === 0 ? statisticCategories : scoring,
      onFetchData() {
        onFetch(challengeId);
      }
    })
  )
);

export default apiWrapper(ChallengeInfoContent);
